import React, { useEffect, useState } from 'react';
import { Info, Megaphone, AlertTriangle, Wrench, CalendarDays, Snowflake, Sun, PartyPopper, Droplets, Trash2, KeyRound, Heart } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Icon- und Farbauswahl der Kunden-Infoleiste (Admin wählt, Kunde sieht nur Text/Icon/Bild)
export const INFO_ICONS = {
  info: { Icon: Info, label: 'Info' },
  megaphone: { Icon: Megaphone, label: 'Durchsage' },
  alert: { Icon: AlertTriangle, label: 'Achtung' },
  wrench: { Icon: Wrench, label: 'Wartung' },
  calendar: { Icon: CalendarDays, label: 'Termin' },
  snowflake: { Icon: Snowflake, label: 'Winter' },
  sun: { Icon: Sun, label: 'Sommer' },
  party: { Icon: PartyPopper, label: 'Feier' },
  droplets: { Icon: Droplets, label: 'Wasser' },
  trash: { Icon: Trash2, label: 'Müll' },
  key: { Icon: KeyRound, label: 'Schlüssel' },
  heart: { Icon: Heart, label: 'Dank' },
};

export const INFO_FARBEN = {
  navy: { label: 'Dunkelblau', bar: 'bg-brand-navy text-white border-brand-navy', icon: 'bg-white/10 text-brand-gold', swatch: 'bg-brand-navy' },
  gold: { label: 'Gold', bar: 'bg-brand-gold/15 text-brand-navy border-brand-gold/40', icon: 'bg-brand-gold/25 text-brand-navy', swatch: 'bg-brand-gold' },
  blue: { label: 'Hellblau', bar: 'bg-sky-50 text-sky-950 border-sky-200', icon: 'bg-sky-100 text-sky-700', swatch: 'bg-sky-400' },
  amber: { label: 'Gelb', bar: 'bg-amber-50 text-amber-950 border-amber-200', icon: 'bg-amber-100 text-amber-700', swatch: 'bg-amber-400' },
  green: { label: 'Grün', bar: 'bg-emerald-50 text-emerald-950 border-emerald-200', icon: 'bg-emerald-100 text-emerald-700', swatch: 'bg-emerald-500' },
  red: { label: 'Rot', bar: 'bg-red-50 text-red-950 border-red-200', icon: 'bg-red-100 text-red-700', swatch: 'bg-red-500' },
  gray: { label: 'Grau', bar: 'bg-gray-100 text-gray-800 border-gray-200', icon: 'bg-white text-gray-600', swatch: 'bg-gray-400' },
};

const imgSrc = (url) => (url && url.startsWith('/') ? `${API}${url}` : url);

// Ein Eintrag: nur Icon, Text und optional Bild – bewusst ohne Datum, Absender oder sonstige Metadaten
export const InfoLeisteItem = ({ item, compact = false }) => {
  const { Icon } = INFO_ICONS[item.icon] || INFO_ICONS.info;
  const farbe = INFO_FARBEN[item.farbe] || INFO_FARBEN.navy;
  const hasText = !!(item.text && item.text.trim());
  return (
    <div className={`border-b ${farbe.bar}`} data-testid={`kunden-info-item-${item.id || 'preview'}`}>
      <div className={`${compact ? '' : 'max-w-5xl mx-auto'} px-4 lg:px-6 py-3 flex items-start gap-3`}>
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${farbe.icon}`}><Icon className="w-5 h-5" /></div>
        <div className="flex-1 min-w-0 flex flex-col sm:flex-row sm:items-center gap-3">
          {hasText && <p className="text-sm leading-relaxed whitespace-pre-line break-words flex-1" data-testid="kunden-info-text">{item.text}</p>}
          {item.image_url && (
            <a href={imgSrc(item.image_url)} target="_blank" rel="noreferrer" className="flex-shrink-0 self-start sm:self-center" data-testid="kunden-info-image-link">
              <img src={imgSrc(item.image_url)} alt="" className={`rounded-lg object-cover border border-black/10 ${hasText ? 'max-h-24 sm:max-h-20 max-w-full sm:max-w-[200px]' : 'max-h-56 max-w-full'}`} data-testid="kunden-info-image" />
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

// Leiste direkt unter dem Menü – nur im Kundenbereich eingebunden
export const KundenInfoLeiste = () => {
  const [items, setItems] = useState([]);
  useEffect(() => {
    let alive = true;
    fetch(`${API}/api/settings/kunden-infoleiste`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (alive && d) setItems((d.items || []).filter(i => i.active !== false)); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  if (!items.length) return null;
  return (
    <div data-testid="kunden-infoleiste" className="flex-shrink-0">
      {items.map(i => <InfoLeisteItem key={i.id} item={i} />)}
    </div>
  );
};

export default KundenInfoLeiste;
